import React, { useMemo, useState, useRef, useEffect } from 'react';

import {
  Box,
  Chip,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { InputAdornment, Checkbox } from '@mui/material';

export type Row = {
  id: number;
  diaSemana: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  horaInicio: string; // HH:mm
  horaFin: string; // HH:mm
  comentario?: string | null;
};

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const STEP = 15; // minutos por columna

// Utils
const toMinutes = (hms: string) => {
  const [h, m] = hms.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};

const mmToHHMM = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

interface WeeklyBarsStaffProps {
  rows: Row[];
  title?: string;
  heightPerLane?: number;
  laneGap?: number;
  dayGap?: number;

  // Callbacks para operaciones CRUD
  onCreate?: (row: Omit<Row, 'id'>) => Promise<void>;
  onUpdate?: (id: number, row: Partial<Row>) => Promise<void>;
  onDelete?: (id: number) => Promise<void>;

  // Usuario seleccionado (para crear nuevas filas)
  idUsuario?: number;
}

export default function WeeklyBarsStaff({
  rows,
  title = 'Disponibilidad Semanal',
  heightPerLane = 40,
  laneGap = 8,
  dayGap = 12,
  onCreate,
  onUpdate,
  onDelete,
  idUsuario,
}: WeeklyBarsStaffProps) {
  const { t } = useTranslation<'translation'>();
  const [editingRow, setEditingRow] = useState<Row | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formDefault, setFormDefault] = useState<Omit<Row, 'id'> | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  //Para hacer las barras dinámicas
  const dayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const suppressCreateRef = useRef<number>(0);
  const [drag, setDrag] = useState<{
    kind: 'move' | 'resize-start' | 'resize-end';
    row: Row;
    startMin: number;
    endMin: number;
    offset?: number;
  } | null>(null);

  const minuteFromXYInDay = (clientX: number, day: number) => {
    const el = dayRefs.current[day] as HTMLDivElement | null;
    const rect = el?.getBoundingClientRect();
    const width = rect?.width ?? 1;
    const left = rect?.left ?? 0;
    const x = Math.min(Math.max(clientX - left, 0), width);
    const perc = x / width;
    return Math.round(perc * 1440);
  };

  // Agrupar filas por día
  const byDay = useMemo(() => {
    const groups: Record<number, Row[]> = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    rows.forEach((row) => {
      groups[row.diaSemana].push(row);
    });
    return groups;
  }, [rows]);

  // Calcular carriles para evitar solapes visuales
  const dayLanes = useMemo(() => {
    const lanes: Record<number, Row[][]> = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };

    Object.entries(byDay).forEach(([diaStr, dayRows]) => {
      const dia = parseInt(diaStr);

      // Crear un objeto intermedio con las propiedades start y end
      type RowWithTime = Row & { start: number; end: number };

      const items: RowWithTime[] = dayRows.map((r) => ({
        ...r,
        start: toMinutes(r.horaInicio),
        end: toMinutes(r.horaFin),
      }));

      items.sort((a, b) => a.start - b.start || a.end - b.end);

      const dayLanes: RowWithTime[][] = [];
      for (const item of items) {
        let placed = false;
        for (const lane of dayLanes) {
          const last = lane[lane.length - 1];
          if (last && last.end <= item.start) {
            lane.push(item);
            placed = true;
            break;
          }
        }
        if (!placed) {
          dayLanes.push([item]);
        }
      }
      lanes[dia] = dayLanes;
    });

    return lanes;
  }, [byDay]);

  // Calcular altura total para cada día
  const dayHeights = useMemo(() => {
    const heights: Record<number, number> = {};
    Object.entries(dayLanes).forEach(([diaStr, lanes]) => {
      const dia = parseInt(diaStr);
      heights[dia] =
        lanes.length > 0
          ? lanes.length * heightPerLane + (lanes.length - 1) * laneGap
          : heightPerLane;
    });
    return heights;
  }, [dayLanes, heightPerLane, laneGap]);

  // Colores para las barras
  const barColors = [
    '#21A598', // Verde principal
    '#1c8f84', // Verde oscuro
    '#26a69a', // Verde claro
    '#4db6ac', // Verde más claro
  ];

  const getBarColor = (laneIndex: number) => {
    return barColors[laneIndex % barColors.length];
  };

  const handleEdit = (row: Row) => {
    setEditingRow(row);
  };

  const handleDelete = async (row: Row) => {
    if (!onDelete) return;

    if (!window.confirm('¿Está seguro de eliminar esta franja horaria?')) {
      return;
    }

    try {
      await onDelete(row.id);
      showSnackbar('Franja eliminada correctamente', 'success');
    } catch (error) {
      showSnackbar('Error al eliminar la franja', 'error');
    }
  };
  const handleCreate = () => {
    if (suppressCreateRef.current > Date.now()) return;
    if (!idUsuario) {
      showSnackbar('Seleccione un usuario primero', 'warning');
      return;
    }
    setEditingRow(null);
    setCreateDialogOpen(true);
  };

  const handleSave = async (formData: Omit<Row, 'id'>) => {
    try {
      if (editingRow) {
        if (onUpdate) {
          await onUpdate(editingRow.id, formData);
          showSnackbar('Franja actualizada correctamente', 'success');
        }
      } else {
        if (onCreate) {
          await onCreate(formData);
          showSnackbar('Franja creada correctamente', 'success');
        }
      }
      setEditingRow(null);
      setCreateDialogOpen(false);
    } catch (error) {
      showSnackbar('Error al guardar la franja', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateEmpty = (e: React.MouseEvent, dia: number) => {
    if (!idUsuario) {
      showSnackbar('Seleccione un usuario primero', 'warning');
      return;
    }

    // ❌ Si se hace clic en una barra → no crear
    if ((e.target as HTMLElement).closest('.availability-bar')) return;

    const trackRect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const width = trackRect.width;

    // Posición X → porcentaje → minutos del día
    const perc = clickX / width;
    const minute = Math.round(perc * 1440); // 24h → 1440 min

    // Ajustar a la rejilla (STEP = 15 min)
    const startMin = Math.floor(minute / STEP) * STEP;
    const endMin = startMin + STEP * 2; // duración mínima = 30 min

    // Abrir formulario precargado
    setEditingRow(null);
    setFormDefault({
      diaSemana: dia as 0 | 1 | 2 | 3 | 4 | 5 | 6,
      horaInicio: mmToHHMM(startMin),
      horaFin: mmToHHMM(endMin),
      comentario: '',
    });
    setCreateDialogOpen(true);
  };

  React.useEffect(() => {
    if (!drag) return;

    const onMove = (e: MouseEvent) => {
      const minute = minuteFromXYInDay(e.clientX, drag.row.diaSemana);

      if (drag.kind === 'move') {
        const delta = minute - (drag.offset ?? 0);
        const newStart = Math.floor(delta / STEP) * STEP;
        const newEnd = drag.endMin + (newStart - drag.startMin);

        setDrag({
          ...drag,
          startMin: newStart,
          endMin: newEnd,
        });
      }

      if (drag.kind === 'resize-start') {
        const newStart = Math.floor(minute / STEP) * STEP;
        if (newStart < drag.endMin) {
          setDrag({
            ...drag,
            startMin: newStart,
          });
        }
      }

      if (drag.kind === 'resize-end') {
        const newEnd = Math.floor(minute / STEP) * STEP;
        if (newEnd > drag.startMin) {
          setDrag({
            ...drag,
            endMin: newEnd,
          });
        }
      }
    };

    const onUp = () => {
      if (drag.kind === 'move' || drag.kind === 'resize-start' || drag.kind === 'resize-end') {
        onUpdate?.(drag.row.id, {
          horaInicio: mmToHHMM(drag.startMin),
          horaFin: mmToHHMM(drag.endMin),
        });
      }
      setDrag(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, onUpdate]);
  return (
    <>
      <Box
        sx={{
          width: '100%',
          border: 1,
          borderColor: '#FFFFFF',
          borderRadius: 2,
          p: 2,
          backgroundColor: '#FFFFFF',
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#21A598' }}>
            {title}
          </Typography>
          {idUsuario && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{
                backgroundColor: '#21A598',
                '&:hover': { backgroundColor: '#1c8f84' },
              }}
            >
              Agregar Franja
            </Button>
          )}
        </Stack>

        {/* Grid principal */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr' }}>
          {/* Columna izquierda (días) */}
          <Box>
            {/* Espaciador para cabecera de horas */}
            <Box sx={{ height: 52 }} />
            <Stack spacing={dayGap / 8}>
              {[0, 1, 2, 3, 4, 5, 6].map((dia) => (
                <Box
                  key={`label-${dia}`}
                  sx={{
                    height: dayHeights[dia],
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      height: `${heightPerLane}px`,
                      lineHeight: `${heightPerLane}px`,
                      px: 1.25,
                      fontWeight: 700,
                      fontSize: 13,
                      bgcolor: '#26a69a',
                      color: 'white',
                      borderRadius: 1,
                      width: '100px',
                      textAlign: 'center',
                    }}
                  >
                    {DAY_NAMES[dia]}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Columna derecha con scroll */}
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ width: '1440px' }}>
              {' '}
              {/* 24 horas * 60px */}
              {/* Cabecera de horas */}
              <Box sx={{ position: 'relative', height: 52 }}>
                {HOURS.map((hour) => (
                  <Box
                    key={`hour-${hour}`}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      left: `${(hour / 24) * 100}%`,
                      transform: 'translateX(-50%)',
                      textAlign: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      {`${String(hour).padStart(2, '0')}:00`}
                    </Typography>
                  </Box>
                ))}

                {/* Líneas verticales */}
                {[...HOURS, 24].map((hour) => (
                  <Box
                    key={`line-${hour}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${(hour / 24) * 100}%`,
                      width: 0,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      transform: 'translateX(-0.5px)',
                      pointerEvents: 'none',
                    }}
                  />
                ))}
              </Box>
              {/* Barras */}
              <Box sx={{ position: 'relative', mt: 0 }}>
                {/* Líneas verticales de fondo */}
                {[...HOURS, 24].map((hour) => (
                  <Box
                    key={`bg-line-${hour}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${(hour / 24) * 100}%`,
                      width: 0,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      transform: 'translateX(-0.5px)',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  />
                ))}

                <Stack spacing={dayGap / 8}>
                  {[0, 1, 2, 3, 4, 5, 6].map((dia) => (
                    <Box
                      key={`day-${dia}`}
                      ref={(el: HTMLDivElement | null) => (dayRefs.current[dia] = el)}
                      sx={{ position: 'relative', height: dayHeights[dia] }}
                      onClick={(e) => handleCreateEmpty(e, dia)}
                    >
                      {dayLanes[dia].map((lane, laneIndex) =>
                        lane.map((row) => {
                          const startPercent = (toMinutes(row.horaInicio) / 1440) * 100;
                          const widthPercent =
                            ((toMinutes(row.horaFin) - toMinutes(row.horaInicio)) / 1440) * 100;
                          const top = laneIndex * (heightPerLane + laneGap);
                          const color = getBarColor(laneIndex);

                          return (
                            <Tooltip
                              key={`row-${row.id}`}
                              title={
                                <Box sx={{ p: 0.5 }}>
                                  <div>
                                    <strong>{DAY_NAMES[dia]}</strong>
                                  </div>
                                  <div>
                                    {row.horaInicio.slice(0, 5)} - {row.horaFin.slice(0, 5)}
                                  </div>
                                  {row.comentario && (
                                    <div>
                                      <em>{row.comentario}</em>
                                    </div>
                                  )}
                                </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <Box
                                className="availability-bar"
                                sx={{
                                  position: 'absolute',
                                  top: `${top}px`,
                                  left: `${startPercent}%`,
                                  width: `${widthPercent}%`,
                                  height: `${heightPerLane}px`,
                                  bgcolor: color,
                                  borderRadius: '5px',
                                  border: '1px solid rgba(0, 0, 0, 0.2)',
                                  boxShadow: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  px: 1.5,
                                  overflow: 'hidden',
                                  cursor: 'move',
                                  '&:hover': {
                                    opacity: 0.9,
                                  },
                                }}
                                onMouseDown={(e) => {
                                  if (!idUsuario) return;
                                  e.preventDefault();
                                  e.stopPropagation();

                                  // Si se hizo clic en un botón o handle, no iniciar arrastre
                                  if (
                                    (e.target as HTMLElement).closest('.no-drag') ||
                                    (e.target as HTMLElement).closest('.handle-resize')
                                  ) {
                                    return;
                                  }

                                  suppressCreateRef.current = Date.now() + 300;

                                  const [start, end] = [
                                    toMinutes(row.horaInicio),
                                    toMinutes(row.horaFin),
                                  ];
                                  const pointerStart = minuteFromXYInDay(e.clientX, row.diaSemana);
                                  const offset = pointerStart - start;

                                  setDrag({
                                    kind: 'move',
                                    row,
                                    startMin: start,
                                    endMin: end,
                                    offset,
                                  });
                                }}
                              >
                                {/* Handle izquierda */}
                                <Box
                                  className="handle-resize"
                                  sx={{
                                    position: 'absolute',
                                    left: -6,
                                    top: '15%',
                                    bottom: '15%',
                                    width: 10,
                                    borderRadius: '6px',
                                    border: '2px solid rgba(0,0,0,0.35)',
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                    cursor: 'ew-resize',
                                    zIndex: 25,
                                  }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    suppressCreateRef.current = Date.now() + 300;
                                    setDrag({
                                      kind: 'resize-start',
                                      row,
                                      startMin: toMinutes(row.horaInicio),
                                      endMin: toMinutes(row.horaFin),
                                    });
                                  }}
                                />

                                {/* Handle derecha */}
                                <Box
                                  className="handle-resize"
                                  sx={{
                                    position: 'absolute',
                                    right: -6,
                                    top: '15%',
                                    bottom: '15%',
                                    width: 10,
                                    borderRadius: '6px',
                                    border: '2px solid rgba(0,0,0,0.35)',
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                    cursor: 'ew-resize',
                                    zIndex: 25,
                                  }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    suppressCreateRef.current = Date.now() + 300;
                                    setDrag({
                                      kind: 'resize-end',
                                      row,
                                      startMin: toMinutes(row.horaInicio),
                                      endMin: toMinutes(row.horaFin),
                                    });
                                  }}
                                />

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: 'white',
                                    textShadow: '1px 1px 1px rgba(0,0,0,0.3)',
                                  }}
                                >
                                  {row.horaInicio.slice(0, 5)} - {row.horaFin.slice(0, 5)}
                                </Typography>

                                <Stack direction="row" spacing={0.5}>
                                  {/* Editar - Añade className="no-drag" */}
                                  <IconButton
                                    className="no-drag"
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleEdit(row);
                                    }}
                                    sx={{
                                      color: 'white',
                                      bgcolor: 'rgba(255,255,255,0.2)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>

                                  {/* Eliminar - Añade className="no-drag" */}
                                  <IconButton
                                    className="no-drag"
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDelete(row);
                                    }}
                                    sx={{
                                      color: 'white',
                                      bgcolor: 'rgba(255,255,255,0.2)',
                                      '&:hover': { bgcolor: 'rgba(255,0,0,0.3)' },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Box>
                            </Tooltip>
                          );
                        }),
                      )}

                      {/* 🔹Barra fantasma mientras arrastras */}
                      {drag &&
                        drag.row.diaSemana === dia &&
                        (() => {
                          const start = drag.startMin;
                          const end = drag.endMin;
                          const startPercent = (start / 1440) * 100;
                          const widthPercent = ((end - start) / 1440) * 100;

                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: `${startPercent}%`,
                                width: `${widthPercent}%`,
                                height: `${heightPerLane}px`,
                                borderRadius: '6px',
                                border: '2px dashed rgba(33,165,152,.6)',
                                background:
                                  'repeating-linear-gradient(45deg, rgba(33,165,152,.25), rgba(33,165,152,.25) 8px, rgba(33,165,152,.12) 8px, rgba(33,165,152,.12) 16px)',
                                pointerEvents: 'none',
                                zIndex: 10,
                              }}
                            />
                          );
                        })()}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Diálogo de creación/edición */}
      <FormDialog
        open={editingRow !== null || createDialogOpen}
        row={editingRow}
        idUsuario={idUsuario}
        formDefault={formDefault}
        onClose={() => {
          setEditingRow(null);
          setCreateDialogOpen(false);
          setFormDefault(null);
        }}
        onSave={handleSave}
      />

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// Componente del formulario
/** 🔹Formulario de creación / edición de franjas */
interface FormDialogProps {
  open: boolean;
  row: Row | null;
  idUsuario?: number;
  formDefault: Omit<Row, 'id'> | null;
  onClose: () => void;
  onSave: (data: Omit<Row, 'id'>) => Promise<void>;
}

const WEEK_DAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function FormDialog({ open, row, idUsuario, formDefault, onClose, onSave }: FormDialogProps) {
  // ⬇️ AHORA usamos array para permitir varios días
  const [form, setForm] = React.useState<{
    diasSemana: number[];
    horaInicio: string;
    horaFin: string;
    comentario: string;
  }>({
    diasSemana: [],
    horaInicio: '09:00',
    horaFin: '17:00',
    comentario: '',
  });

  // ⬇️ Al abrir el diálogo cargamos datos según si es edición o creación
  React.useEffect(() => {
    if (!open) return;

    if (row) {
      setForm({
        diasSemana: [row.diaSemana],
        horaInicio: row.horaInicio,
        horaFin: row.horaFin,
        comentario: row.comentario ?? '',
      });
    } else if (formDefault) {
      setForm({
        diasSemana: [formDefault.diaSemana],
        horaInicio: formDefault.horaInicio,
        horaFin: formDefault.horaFin,
        comentario: formDefault.comentario ?? '',
      });
    } else {
      setForm({
        diasSemana: [],
        horaInicio: '09:00',
        horaFin: '17:00',
        comentario: '',
      });
    }
  }, [open, row, formDefault]);

  // ⬇️ Cambiar check del día → añadir / quitar del array
  const toggleDay = (day: number) => {
    setForm((prev) => {
      const selected = prev.diasSemana.includes(day)
        ? prev.diasSemana.filter((d) => d !== day)
        : [...prev.diasSemana, day];
      return { ...prev, diasSemana: selected };
    });
  };

  const handleSubmit = async () => {
    if (!idUsuario && !row) {
      alert('Seleccione un usuario primero');
      return;
    }

    if (toMinutes(form.horaInicio) >= toMinutes(form.horaFin)) {
      alert('La hora de fin debe ser mayor');
      return;
    }

    if (form.diasSemana.length === 0) {
      alert('Seleccione al menos un día');
      return;
    }

    // 🔹 Modo edición → solo 1 día
    if (row) {
      await onSave({
        diaSemana: form.diasSemana[0] as any,
        horaInicio: form.horaInicio,
        horaFin: form.horaFin,
        comentario: form.comentario,
      });
      onClose();
      return;
    }

    // 🔹 Modo creación → crear 1 franja por día marcado
    for (const dia of form.diasSemana) {
      await onSave({
        diaSemana: dia as any,
        horaInicio: form.horaInicio,
        horaFin: form.horaFin,
        comentario: form.comentario,
      });
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: '#21A598', color: 'white', fontWeight: 700 }}>
        Añadir franjas
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* 🔹 Selección de días múltiple */}
        <Stack direction="row" justifyContent="center" spacing={1}>
          {WEEK_DAYS.map((d, i) => (
            <Stack key={i} alignItems="center">
              <Checkbox
                disabled={!!row} // ⬅️ si estamos editando → bloqueado
                checked={form.diasSemana.includes(i)}
                onChange={() => !row && toggleDay(i)} // ⬅️ evita cambios al editar
                sx={{
                  color: '#21A598',
                  '&.Mui-checked': { color: '#21A598' },
                  p: 0.4,
                }}
              />
              <Typography fontWeight={700}>{d}</Typography>
            </Stack>
          ))}
        </Stack>

        {/* 🔹 Horarios */}
        <Stack direction="row" spacing={2} mt={3}>
          <TextField
            label="Hora inicio"
            type="time"
            fullWidth
            value={form.horaInicio}
            onChange={(e) => setForm({ ...form, horaInicio: e.target.value })}
            InputLabelProps={{ shrink: true }}
            InputProps={{}}
          />

          <TextField
            label="Hora fin"
            type="time"
            fullWidth
            value={form.horaFin}
            onChange={(e) => setForm({ ...form, horaFin: e.target.value })}
            InputLabelProps={{ shrink: true }}
            InputProps={{}}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button type="button" onClick={onClose} sx={{ color: '#21A598' }}>
          CANCELAR
        </Button>

        <Button
          type="button"
          variant="contained"
          onClick={handleSubmit}
          sx={{
            bgcolor: '#21A598',
            fontWeight: 700,
            '&:hover': { bgcolor: '#1c8f84' },
          }}
        >
          GUARDAR
        </Button>
      </DialogActions>
    </Dialog>
  );
}
